/** @jsxRuntime classic */
/** @jsx jsx */
// @ts-check

import { Box, Button, Flex, Text } from '@bottlebooks/gatsby-theme-base';
import React from 'react';
import { jsx } from 'theme-ui';
import { gql } from 'urql';
import { TicketButtonFragment } from '../../../@bottlebooks/gatsby-theme-event/components/Layout/TicketButton';
import { useEventThemeFragment } from '../../../@bottlebooks/gatsby-theme-event/components/Layout/useEventTheme';
import { useFooterQueryFragment } from '../../../@bottlebooks/gatsby-theme-event/components/Layout/useFooterQuery';
import useMenuQuery from '../../../@bottlebooks/gatsby-theme-event/components/Layout/useMenuQuery';
import { ErrorSection, SanityPage } from '../../../components';
import usePreviewData, { PreviewDataProvider } from '../../../usePreviewData';

export default function PreviewSanityPageWrapper(props) {
  const {
    pageId,
    pathContext: { collectionId },
  } = props;
  return (
    <PreviewDataProvider
      query={clientQuery}
      variables={{ pageId, draftPageId: `drafts.${pageId}`, collectionId }}
    >
      <PreviewSanityPage {...props} />
    </PreviewDataProvider>
  );
}

function PreviewSanityPage(props) {
  const [result, reexecuteQuery] = usePreviewData();
  // We have to use a manual polling strategy here because Urql doesn't have native support
  const [counter, setCounter] = React.useState(0);
  React.useEffect(() => {
    setTimeout(() => {
      reexecuteQuery();
      setCounter(counter + 1);
    }, 2000);
  }, [counter, reexecuteQuery]);

  const { data, fetching, error } = result;
  const site = data?.allSite?.[0];

  if (!data.Page && !data.draft && fetching)
    return (
      <Flex align="center" justify="center" sx={{ height: '100vh' }}>
        <Text>Loading Preview</Text>
      </Flex>
    );

  if (error) {
    return (
      <ErrorSection error={error}>
        <Text>
          Potential solution: Check that CORS is active for this site. Ensure
          that 'Allow Credentials' is checked.
        </Text>
        <Box sx={{ marginTop: 2 }}>
          <Button
            variant="primary"
            href={`https://www.sanity.io/manage/project/${process.env.GATSBY_SANITY_PROJECT_ID}/api`}
            target="_blank"
          >
            <Text>Go to Sanity project</Text>
          </Button>
        </Box>
      </ErrorSection>
    );
  }

  if (!data.Page && !data.draft)
    return (
      <ErrorSection error={{ message: 'Page not found' }}>
        <Text>
          Confirm that you are logged into your CMS, or this page doesn't exist.
          Try reloading this page preview from the CMS.
        </Text>
      </ErrorSection>
    );

  const { slug } = afterQuery(data);
  return (
    <Box>
      <Flex
        align="center"
        justify="center"
        sx={{ paddingY: 3, backgroundColor: '#f3f3f3' }}
      >
        <Text>
          This is a preview.{'  '}
          <a href={slug?.current !== 'index' ? `/${slug?.current}` : '/'}>
            View live version.
          </a>
        </Text>
      </Flex>
      <SanityPage {...afterQuery(data)} />
    </Box>
  );
}

function afterQuery({ Page, draft }) {
  return draft || Page;
}

const clientQuery = gql`
  query ($pageId: ID!, $draftPageId: ID!, $collectionId: String!) {
    allSite(where: { collectionId: { eq: $collectionId } }) {
      frontpage {
        __typename
        id: _id
        title
        slug {
          current
        }
      }
      ...useEventThemeFragment
      ...useFooterQueryFragment
      ...useMenuQueryFragment
      ...TicketButtonFragment
    }
    Page(id: $pageId) {
      ...SanityPage
    }
    draft: Page(id: $draftPageId) {
      ...SanityPage
    }
  }
  ${SanityPage.fragment}
  ${useMenuQuery.fragment}
  ${useFooterQueryFragment}
  ${TicketButtonFragment}
  ${useEventThemeFragment}
`;
